import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { User, Package, Calendar, MapPin, Phone, Mail, LogOut, ShoppingBag } from "lucide-react";
import { Link } from "react-router-dom";

export default function Profile() {
  const { 
    user, 
    isAuthenticated, 
    loginWithGoogle, 
    loginWithEmail, 
    signUpWithEmail, 
    forgotPassword, 
    resetPassword, 
    logout, 
    loading 
  } = useAuth();
  
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  // Custom auth states
  const [authTab, setAuthTab] = useState("login"); // "login" | "signup" | "forgot"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [forgotStep, setForgotStep] = useState(1); // 1: enter email, 2: enter otp & new pass
  const [submitting, setSubmitting] = useState(false);

  // Initialize Google Sign-In when not authenticated
  useEffect(() => {
    if (loading || isAuthenticated) return;

    let checkGoogleInterval = setInterval(() => {
      if (window.google) {
        clearInterval(checkGoogleInterval);
        try {
          const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "1027150821034-mockclientid123.apps.googleusercontent.com";
          window.google.accounts.id.initialize({
            client_id: clientId,
            callback: async (response) => {
              try {
                await loginWithGoogle(response.credential);
                toast.success("Welcome to your sanctuary.");
              } catch (err) {
                console.error(err);
                toast.error("Could not authenticate with Google. Please try again.");
              }
            },
          });

          const btnElem = document.getElementById("google-signin-btn");
          if (btnElem) {
            window.google.accounts.id.renderButton(btnElem, {
              theme: "outline",
              size: "large",
              width: 320,
              type: "standard",
              shape: "rectangular",
              text: "signin_with",
              logo_alignment: "left",
            });
          }
        } catch (e) {
          console.error("Error initializing Google Identity:", e);
        }
      }
    }, 100);

    return () => clearInterval(checkGoogleInterval);
  }, [isAuthenticated, loading, loginWithGoogle, authTab]);

  // Fetch orders when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchOrders = async () => {
      setLoadingOrders(true);
      try {
        const { data } = await api.get("/orders/my");
        setOrders(data);
      } catch (err) {
        toast.error("Could not load your order history.");
      } finally {
        setLoadingOrders(false);
      }
    };

    fetchOrders();
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }
    setSubmitting(true);
    try {
      await loginWithEmail(email, password);
      toast.success("Welcome back to your sanctuary.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.error("Please fill in all fields");
      return;
    }
    setSubmitting(true);
    try {
      await signUpWithEmail(name, email, password);
      toast.success("Account created! Welcome to your sanctuary.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }
    setSubmitting(true);
    try {
      const res = await forgotPassword(email);
      toast.success(res.message || "Verification code sent to email.");
      setForgotStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reset code.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!email || !otp || !newPassword) {
      toast.error("Please enter OTP code and new password");
      return;
    }
    setSubmitting(true);
    try {
      const res = await resetPassword(email, otp, newPassword);
      toast.success(res.message || "Password updated successfully.");
      setAuthTab("login");
      setForgotStep(1);
      setPassword("");
      setOtp("");
      setNewPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset password. Check the OTP code.");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusDetails = (status) => {
    switch (status) {
      case "inquiry_received":
        return { label: "Inquiry Received", bg: "bg-[#FAF1EC]", text: "text-[#C86D4B]", border: "border-[#FAF1EC]" };
      case "confirmed":
        return { label: "Confirmed", bg: "bg-[#EAF2EC]", text: "text-[#3A5A40]", border: "border-[#EAF2EC]" };
      case "shipped":
        return { label: "Shipped", bg: "bg-[#EEF1F6]", text: "text-[#3B6088]", border: "border-[#EEF1F6]" };
      case "completed":
        return { label: "Completed", bg: "bg-[#F7F5E8]", text: "text-[#706D34]", border: "border-[#F7F5E8]" };
      case "cancelled":
        return { label: "Cancelled", bg: "bg-[#FAEAF0]", text: "text-[#A8324A]", border: "border-[#FAEAF0]" };
      default:
        return { label: status, bg: "bg-gray-100", text: "text-gray-700", border: "border-gray-100" };
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: "var(--ayu-primary) transparent transparent transparent" }}></div>
        <p className="mt-4 text-sm tracking-wide text-[var(--ayu-muted)]">Aligning credentials...</p>
      </div>
    );
  }

  // Not Logged In State
  if (!isAuthenticated) {
    return (
      <div className="min-h-[85vh] px-6 md:px-12 lg:px-20 py-24 flex items-center justify-center bg-[var(--ayu-bg)]">
        <div className="max-w-md w-full bg-white border border-[var(--ayu-border)] p-10 relative overflow-hidden grain shadow-sm rounded-sm">
          <div className="absolute top-0 left-0 right-0 h-1 bg-[var(--ayu-primary)]"></div>
          
          <div className="w-12 h-12 bg-[#FAF8F5] text-[var(--ayu-primary)] flex items-center justify-center mx-auto mb-6 border border-[var(--ayu-border)] rounded-full">
            <User size={20} strokeWidth={1.5} />
          </div>

          <div className="text-center mb-8">
            <p className="overline mb-2">Your Wellness Portal</p>
            <h1 className="font-display text-3xl text-[var(--ayu-heading)]">
              {authTab === "login" && "Sign In to Sanctuary"}
              {authTab === "signup" && "Create an Account"}
              {authTab === "forgot" && "Recover Password"}
            </h1>
          </div>

          {/* Social Sign-In (Google) - Kept for both signup/signin but hidden for forgot password state */}
          {authTab !== "forgot" && (
            <div className="flex flex-col items-center mb-6">
              <div id="google-signin-btn" data-testid="google-signin-button" className="min-h-[40px] w-full flex justify-center"></div>
              
              <div className="w-full flex items-center justify-between my-4 text-xs uppercase tracking-wider text-[var(--ayu-muted)]">
                <span className="w-[42%] border-t border-[var(--ayu-border)]"></span>
                <span>or</span>
                <span className="w-[42%] border-t border-[var(--ayu-border)]"></span>
              </div>
            </div>
          )}

          {/* Authentication Forms */}
          {authTab === "login" && (
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[var(--ayu-heading)] mb-1.5 font-medium">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2.5 bg-[#FAF8F5] border border-[var(--ayu-border)] text-sm focus:outline-none focus:border-[var(--ayu-primary)] transition-colors rounded-sm"
                  placeholder="Enter email"
                  required
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <label className="block text-xs uppercase tracking-wider text-[var(--ayu-heading)] font-medium">Password</label>
                  <button
                    type="button"
                    onClick={() => {
                      setAuthTab("forgot");
                      setForgotStep(1);
                    }}
                    className="text-xs text-[var(--ayu-primary)] hover:underline"
                  >
                    Forgot?
                  </button>
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2.5 bg-[#FAF8F5] border border-[var(--ayu-border)] text-sm focus:outline-none focus:border-[var(--ayu-primary)] transition-colors rounded-sm"
                  placeholder="Enter password"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full justify-center py-3 text-xs uppercase tracking-wider"
              >
                {submitting ? "Signing In..." : "Sign In"}
              </button>
              <div className="text-center text-xs text-[var(--ayu-muted)] mt-4">
                Don't have an account?{" "}
                <button
                  type="button"
                  onClick={() => setAuthTab("signup")}
                  className="text-[var(--ayu-primary)] hover:underline font-semibold"
                >
                  Create one
                </button>
              </div>
            </form>
          )}

          {authTab === "signup" && (
            <form onSubmit={handleSignUp} className="space-y-5">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[var(--ayu-heading)] mb-1.5 font-medium">Full Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-2.5 bg-[#FAF8F5] border border-[var(--ayu-border)] text-sm focus:outline-none focus:border-[var(--ayu-primary)] transition-colors rounded-sm"
                  placeholder="Enter your name"
                  required
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[var(--ayu-heading)] mb-1.5 font-medium">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2.5 bg-[#FAF8F5] border border-[var(--ayu-border)] text-sm focus:outline-none focus:border-[var(--ayu-primary)] transition-colors rounded-sm"
                  placeholder="Enter email"
                  required
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[var(--ayu-heading)] mb-1.5 font-medium">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2.5 bg-[#FAF8F5] border border-[var(--ayu-border)] text-sm focus:outline-none focus:border-[var(--ayu-primary)] transition-colors rounded-sm"
                  placeholder="Create password"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full justify-center py-3 text-xs uppercase tracking-wider"
              >
                {submitting ? "Creating Account..." : "Create Account"}
              </button>
              <div className="text-center text-xs text-[var(--ayu-muted)] mt-4">
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => setAuthTab("login")}
                  className="text-[var(--ayu-primary)] hover:underline font-semibold"
                >
                  Sign In
                </button>
              </div>
            </form>
          )}

          {authTab === "forgot" && (
            <div>
              {forgotStep === 1 ? (
                <form onSubmit={handleForgotPassword} className="space-y-5">
                  <p className="text-xs text-[var(--ayu-muted)] leading-relaxed mb-4 text-center">
                    Enter the email address associated with your sanctuary account. We will send you a 6-digit OTP code to verify your identity.
                  </p>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[var(--ayu-heading)] mb-1.5 font-medium">Email Address</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-2.5 bg-[#FAF8F5] border border-[var(--ayu-border)] text-sm focus:outline-none focus:border-[var(--ayu-primary)] transition-colors rounded-sm"
                      placeholder="Enter email"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary w-full justify-center py-3 text-xs uppercase tracking-wider"
                  >
                    {submitting ? "Sending Code..." : "Send Verification Code"}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-5">
                  <p className="text-xs text-[var(--ayu-muted)] leading-relaxed mb-4 text-center">
                    Enter the 6-digit code sent to <strong className="text-[var(--ayu-heading)]">{email}</strong> and create your new secure password.
                  </p>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[var(--ayu-heading)] mb-1.5 font-medium">6-Digit Verification Code</label>
                    <input
                      type="text"
                      maxLength={6}
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      className="w-full px-4 py-2.5 bg-[#FAF8F5] border border-[var(--ayu-border)] text-sm text-center font-mono focus:outline-none focus:border-[var(--ayu-primary)] transition-colors rounded-sm"
                      placeholder="XXXXXX"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[var(--ayu-heading)] mb-1.5 font-medium">New Password</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-4 py-2.5 bg-[#FAF8F5] border border-[var(--ayu-border)] text-sm focus:outline-none focus:border-[var(--ayu-primary)] transition-colors rounded-sm"
                      placeholder="Enter new password"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary w-full justify-center py-3 text-xs uppercase tracking-wider"
                  >
                    {submitting ? "Resetting..." : "Reset Password"}
                  </button>
                </form>
              )}
              <div className="text-center text-xs mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setAuthTab("login");
                    setForgotStep(1);
                  }}
                  className="text-[var(--ayu-muted)] hover:text-[var(--ayu-primary)] font-medium transition-colors"
                >
                  &larr; Back to Login
                </button>
              </div>
            </div>
          )}

          <div className="text-[10px] opacity-60 flex items-center justify-center gap-1.5 mt-8 border-t border-[var(--ayu-border)] pt-4" style={{ color: "var(--ayu-muted)" }}>
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--ayu-primary)]"></span>
            All connection lines are encrypted and secure
          </div>
        </div>
      </div>
    );
  }

  // Logged In State
  return (
    <div data-testid="profile-page" className="px-6 md:px-12 lg:px-20 pt-24 pb-24 max-w-7xl mx-auto">
      {/* Top Banner / User Greeting */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start mb-16 pb-12 border-b border-[var(--ayu-border)]">
        <div className="md:col-span-2 flex flex-col md:flex-row items-center md:items-start gap-6 text-center md:text-left">
          {user.picture ? (
            <img 
              src={user.picture} 
              alt={user.name} 
              className="w-20 h-20 rounded-full object-cover border border-[var(--ayu-border)] shadow-sm"
            />
          ) : (
            <div className="w-20 h-20 rounded-full flex items-center justify-center text-3xl font-display text-white bg-[var(--ayu-primary)] shadow-sm">
              {user.name.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="space-y-1.5">
            <span className="overline">Customer Sanctuary</span>
            <h1 className="font-display text-4xl text-[var(--ayu-heading)]">{user.name}</h1>
            <p className="text-sm flex items-center justify-center md:justify-start gap-2" style={{ color: "var(--ayu-body)" }}>
              <Mail size={14} className="opacity-75" /> {user.email}
            </p>
          </div>
        </div>

        <div className="flex md:justify-end justify-center pt-4 md:pt-0">
          <button 
            onClick={logout} 
            data-testid="logout-button"
            className="btn-secondary py-2.5 px-5 text-sm flex items-center gap-2 border border-[var(--ayu-primary)] text-[var(--ayu-primary)] hover:bg-[var(--ayu-primary)] hover:text-white transition-all duration-300"
          >
            <LogOut size={15} />
            <span>Sign Out</span>
          </button>
        </div>
      </div>

      {/* Main Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Left Side: Order Inquiries */}
        <div className="lg:col-span-8 space-y-8">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-3xl flex items-center gap-3">
              <Package size={22} className="text-[var(--ayu-primary)]" strokeWidth={1.5} />
              <span>Apothecary Enquiries</span>
            </h2>
            <span className="text-xs px-2.5 py-1 bg-[var(--ayu-surface)] font-medium text-[var(--ayu-heading)]">
              {orders.length} Order{orders.length !== 1 ? "s" : ""}
            </span>
          </div>

          {loadingOrders ? (
            <div className="py-12 text-center text-sm text-[var(--ayu-muted)]">
              <div className="w-6 h-6 rounded-full border-2 border-t-transparent animate-spin mx-auto mb-3" style={{ borderColor: "var(--ayu-primary) transparent transparent transparent" }}></div>
              Retrieving clinical ledger...
            </div>
          ) : orders.length === 0 ? (
            <div className="border border-dashed border-[var(--ayu-border)] p-12 text-center bg-[var(--ayu-surface)]/10">
              <ShoppingBag size={32} className="mx-auto mb-4 opacity-40 text-[var(--ayu-muted)]" strokeWidth={1.2} />
              <p className="text-sm font-medium mb-1">No orders on file yet.</p>
              <p className="text-xs opacity-75 mb-6 max-w-xs mx-auto" style={{ color: "var(--ayu-muted)" }}>
                Explore our botanical remedies and state-of-the-art equipment to place your first inquiry.
              </p>
              <Link to="/shop/medicines" className="btn-primary py-2 px-5 text-xs">
                Browse Medicines
              </Link>
            </div>
          ) : (
            <div className="space-y-6" data-testid="orders-list">
              {orders.map((order) => {
                const status = getStatusDetails(order.status);
                const orderDate = new Date(order.created_at).toLocaleDateString("en-IN", {
                  year: "numeric", month: "long", day: "numeric"
                });

                return (
                  <div 
                    key={order.id} 
                    data-testid={`order-card-${order.id}`}
                    className="border border-[var(--ayu-border)] bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300"
                  >
                    {/* Header bar of order card */}
                    <div className="px-6 py-4 bg-[var(--ayu-bg)] flex flex-wrap items-center justify-between gap-4 border-b border-[var(--ayu-border)] text-sm">
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-[var(--ayu-heading)]">
                          ID: <span className="font-mono text-[10px] opacity-75">{order.id}</span>
                        </p>
                        <p className="text-[11px] flex items-center gap-1.5 opacity-75" style={{ color: "var(--ayu-muted)" }}>
                          <Calendar size={12} /> {orderDate}
                        </p>
                      </div>
                      <span className={`text-[10px] uppercase font-bold tracking-wider px-3 py-1 rounded-sm border ${status.bg} ${status.text} ${status.border}`}>
                        {status.label}
                      </span>
                    </div>

                    {/* Order details body */}
                    <div className="p-6">
                      <div className="space-y-4 mb-6">
                        {order.items.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-4 text-sm justify-between">
                            <div className="flex items-center gap-3">
                              {item.image ? (
                                <img 
                                  src={item.image} 
                                  alt={item.name} 
                                  className="w-10 h-10 object-cover border border-[var(--ayu-border)]"
                                />
                              ) : (
                                <div className="w-10 h-10 bg-[var(--ayu-surface)] flex items-center justify-center text-xs">
                                  🌿
                                </div>
                              )}
                              <div>
                                <h4 className="font-medium text-[var(--ayu-heading)] leading-tight">{item.name}</h4>
                                <p className="text-xs opacity-75" style={{ color: "var(--ayu-muted)" }}>
                                  ₹{item.price.toLocaleString("en-IN")} × {item.quantity}
                                </p>
                              </div>
                            </div>
                            <span className="font-medium text-[var(--ayu-heading)]">
                              ₹{(item.price * item.quantity).toLocaleString("en-IN")}
                            </span>
                          </div>
                        ))}
                      </div>

                      <div className="border-t border-[var(--ayu-border)] pt-4 flex items-center justify-between">
                        <span className="text-xs uppercase tracking-wider text-[var(--ayu-muted)]">Enquiry Total</span>
                        <span className="font-display text-xl text-[var(--ayu-primary)] font-semibold">
                          ₹{order.total.toLocaleString("en-IN")}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right Side: Account Details Sidebar */}
        <div className="lg:col-span-4 space-y-6">
          <div className="p-8 bg-[var(--ayu-surface)] grain relative overflow-hidden shadow-sm">
            <h3 className="font-display text-2xl mb-6">Sanctuary Address</h3>
            
            {orders.length > 0 ? (
              // Prefill from last order
              <div className="space-y-4 text-sm">
                <div className="flex gap-3">
                  <User size={16} className="text-[var(--ayu-primary)] mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs overline">Consignee</p>
                    <p className="font-medium text-[var(--ayu-heading)]">{orders[0].customer_name}</p>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <MapPin size={16} className="text-[var(--ayu-primary)] mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs overline">Delivery Location</p>
                    <p className="text-[var(--ayu-heading)] leading-relaxed">
                      {orders[0].address},<br />
                      {orders[0].city} - {orders[0].pincode}
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Phone size={16} className="text-[var(--ayu-primary)] mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs overline">Contact Line</p>
                    <p className="text-[var(--ayu-heading)]">{orders[0].phone}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-xs opacity-75 leading-relaxed" style={{ color: "var(--ayu-body)" }}>
                You have not placed any apothecary enquiries yet. Once you place an inquiry, your shipping details and contact information will appear here for easy reference.
              </div>
            )}
          </div>

          {/* Quick FAQ / Wisdom Card */}
          <div className="p-8 border border-[var(--ayu-border)] bg-white text-xs space-y-4">
            <h4 className="overline">Our Inquiry Process</h4>
            <p className="leading-relaxed" style={{ color: "var(--ayu-body)" }}>
              As we customize herbal mixtures and verify traditional specifications for clinical equipment, we process all orders as inquiries first.
            </p>
            <p className="leading-relaxed" style={{ color: "var(--ayu-body)" }}>
              Our expert Vaidyas review item compatibility and reach out to you within 24 hours to confirm delivery timelines, shipping charges, and payment options.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
